/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { jsx } from '@amazon/vinyl-tsx'
import { ShovelerSkeleton } from '@/components/ShovelerSkeleton'
import { TrackRowSkeleton } from '@/components/TrackRowSkeleton'

/**
 * Detail-page-wide skeleton painted between the hero and the resolved
 * body of album / artist / playlist detail pages while the initial
 * fetch is in flight. Without it the space below the hero is blank
 * until the wire lands, which reads as a broken page (especially with
 * the artificial-latency slider set to 1500ms).
 *
 * Composition:
 *   - Optional stack of faux track rows (thin rectangles) for the
 *     playlist page's virtualized tracklist.
 *   - N shoveler-shaped rows (header rectangle + tile row) via the
 *     shared `ShovelerSkeleton` primitive so the loading footprint
 *     matches the resolved shoveler geometry — no layout shift when
 *     data lands.
 *
 * Consumers gate visibility on `<observable>.map(v => v == null)` so
 * the skeleton unmounts the moment the fetch resolves.
 */
export interface DetailPageSkeletonProps {
    /**
     * Number of shoveler-shaped placeholder rows. Defaults to 3 —
     * matches the typical album / artist / playlist detail-page tail
     * (More by artist / Similar / Merch, or Latest / Top / Related).
     */
    readonly rowCount?: number

    /**
     * Number of tile rectangles per shoveler row. Defaults to 5 — the
     * mid-viewport visible count for the LARGE recommendations
     * shoveler.
     */
    readonly tilesPerRow?: number

    /**
     * Render faux track rows above the shovelers. Only the playlist
     * page currently opts in (its main body is a virtualized
     * tracklist, not a header + shovelers stack).
     */
    readonly trackRowCount?: number
}

const DEFAULT_ROW_COUNT = 3
const DEFAULT_TILES_PER_ROW = 5

export function DetailPageSkeleton(props?: DetailPageSkeletonProps | null) {
    const rowCount = props?.rowCount ?? DEFAULT_ROW_COUNT
    const tilesPerRow = props?.tilesPerRow ?? DEFAULT_TILES_PER_ROW
    const trackRowCount = props?.trackRowCount ?? 0

    const shovelerRows: Element[] = []
    for (let i = 0; i < rowCount; i++) {
        shovelerRows.push(<ShovelerSkeleton count={tilesPerRow} />)
    }

    // The shimmer rectangles are decorative, so each shimmer subtree stays
    // aria-hidden (TrackRowSkeleton and ShovelerSkeleton hide their own
    // roots). The enclosing region is NOT aria-hidden — it carries aria-busy so
    // assistive tech is told this area is loading, then goes quiet when the
    // skeleton unmounts and real content takes its place.
    return (
        <div className="detailPageSkeleton" aria-busy="true">
            {trackRowCount > 0 ? <TrackRowSkeleton count={trackRowCount} /> : null}
            {...shovelerRows}
        </div>
    )
}
